import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Dokument } from '../../../../shared/models/dokument';
import { PreviewDataType } from '../../../../shared/enums/preview-data-type.enum';
import { catchError, EMPTY, filter, finalize, tap } from 'rxjs';
import { HttpUtils } from '../../../../shared/utils/http.utils';
import { DokumentApi } from '../../../../shared/services/api/dokument.api';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { ApplicationVisualizationDialog } from '../../../application-sent/components/application-visualization-dialog/application-visualization-dialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DatePipe } from '@angular/common';
import { VisualizationType } from '../../../../shared/enums/visualization-type.enum';
import { ExportType } from '../../../../shared/enums/export-type.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KorespondencjaDialogComponent } from '../../../../shared/components/korespondencja-dialog/korespondencja-dialog.component';
import { VisualizationApi } from '../../../../shared/services/api/visualization.api';
import { ExportApi } from '../../../../shared/services/api/export.api';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { InboxApi } from '../../../../shared/services/api/inbox.api';
import { InboxType } from '../../../../shared/enums/inbox-type.enum';
import { Zalacznik } from '../../../../shared/models/zalacznik';
import { IsZUSDokumentPipe } from './is-zus-dokument.pipe';
import { ShouldShowNoAttachmentsPipe } from './should-show-no-attachments.pipe';
import { TooltipModule } from 'primeng/tooltip';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';
import { ShouldShowCorrespondenceIconPipe } from '../../../../shared/pipes/should-show-correspondence-icon.pipe';

@Component({
  selector: 'app-document-details',
  imports: [
    Card,
    PreviewDataWrapperComponent,
    Button,
    TableModule,
    TooltipModule,
    DatePipe,
    IsZUSDokumentPipe,
    ShouldShowNoAttachmentsPipe,
    ShouldShowCorrespondenceIconPipe,
  ],
  templateUrl: './document-details.component.html',
  styleUrl: './document-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DokumentApi, ExportApi, VisualizationApi, InboxApi],
  standalone: true,
})
export class DocumentDetailsComponent {
  public inboxType = input<InboxType>();
  protected document = input.required<Dokument>();
  protected readonly InboxType = InboxType;
  protected readonly PreviewDataType = PreviewDataType;
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogService = inject(DialogService);
  readonly #exportApi = inject(ExportApi);
  readonly #inboxApi = inject(InboxApi);
  readonly #dialogRef = inject(DynamicDialogRef);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #visualizationApi = inject(VisualizationApi);
  #selectedMessages: Dokument[] = [];
  readonly #spinnerService = inject(SpinnerService);

  correspond(dokument: Dokument) {
    this.#dialogService.open(KorespondencjaDialogComponent, {
      header: `Koresponduj w sprawie`,
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '50vw',
      data: {
        dokument: dokument,
      },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });
  }

  protected downloadAttachment(attachment: Zalacznik): void {
    this.#spinnerService.show();

    this.#exportApi
      .exportApplication$(attachment.id!, ExportType.ATTACHMENT)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się pobrać załącznika',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected exportApplication(): void {
    const id = this.document().id;
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(id, ExportType.APPLICATION)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się wyeksportować dokumentu',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected exportPoswiadczenie(): void {
    const id = this.document().poswiadczenie?.id;
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(id!, ExportType.UPO)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się wyeksportować poświadczenia',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected restoreFromArchiveDocumentDialog(): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz przywrócić dokument z archiwum?`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
      },
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#restoreFromArchiveDocument()),
          takeUntilDestroyed(this.#destroyRef),
          finalize(() => {
            this.#dialogRef.close('received');
          }),
        )
        .subscribe();
    }
  }

  protected showDocumentSignature(): void {
    this.#visualizationApi
      .getVisualization$(this.document().id, VisualizationType.SIGNATURE)
      .pipe(
        tap((content: string) => {
          this.#openVisualizationDialog(content);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected showPoswiadczenieVisualization(): void {
    this.#visualizationApi
      .getVisualization$(this.document().id, VisualizationType.UPO)
      .pipe(
        tap((content: string) => {
          this.#openVisualizationDialog(content);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected archiveDocumentDialog(): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz przenieść wybrany dokument do archiwum?`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
        description:
          'Dokument będzie dostępny po wybraniu filtra ,,Zarchiwizowane".',
      },
    });
    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#archiveDocument()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected showApplicationVisualization(): void {
    this.#visualizationApi
      .getVisualization$(this.document().id, VisualizationType.PO)
      .pipe(
        tap((content: string) => {
          this.#openVisualizationDialog(content);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #restoreFromArchiveDocument(): void {
    const count = this.#selectedMessages?.length ?? 0;

    this.#spinnerService.show();
    this.#inboxApi
      .restoreFromArchiveDocuments$(new Set([this.document().id]))
      .pipe(
        tap(() => {
          const message = 'Poprawnie przywrócono dokument z archiwum.';
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie przywrócono dokument z archiwum',
          );
          return EMPTY;
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby przywrócenia dokumentu z archiwum',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#selectedMessages = [];
          this.#spinnerService.hide();
          this.#dialogRef.close('received');
        }),
      )
      .subscribe();
  }

  #openVisualizationDialog(content: string): void {
    this.#dialogService.open(ApplicationVisualizationDialog, {
      closable: true,
      header: `Szczegóły wniosku`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        ['content']: content,
      },
      style: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: '0',
      },
    });
  }

  #archiveDocument(): void {
    this.#spinnerService.show();
    this.#inboxApi
      .archiveDocuments$(new Set([this.document().id]))
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie przeniesiono dokument do archiwum',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby przeniesienia dokumentu do archiwum',
          );

          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#selectedMessages = [];
          this.#spinnerService.hide();
          this.#dialogRef.close('archived');
        }),
      )
      .subscribe();
  }
}
